import './ShareTargets.scss';
import {
    ClientId, ShareType, OutlookAvailability, ISharingInformation,
    ISharingLinkSettings, SharingAudience
} from '../../../interfaces/SharingInterfaces';
import * as React from 'react';
import * as ClientIdHelper from '../../../utilities/ClientIdHelper';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { isLoadingData } from '../../../utilities/ShareHelper';
import { TooltipHost, DirectionalHint } from 'office-ui-fabric-react/lib/Tooltip';
import stringsResx from '../../../components/Share.resx';

export interface IShareTargetsProps {
    clientId: ClientId;
    currentSettings: ISharingLinkSettings;
    onShareTargetClicked: (shareType: ShareType) => void;
    onShareTargetsRendered: (shareTargets: Array<ShareType>) => void;
    outlookAvailability: OutlookAvailability;
    sharingInformation: ISharingInformation;
}

export interface IShareTargetData {
    label: string;
    icon: string;
    bgColor: string;
    shareType: ShareType;
    action: () => void;
}

export class ShareTargets extends React.Component<IShareTargetsProps, {}> {
    private _shareTargetsData: Array<IShareTargetData>;

    public render(): React.ReactElement<{}> {
        const accessRequestsRequired = this.props.sharingInformation.requiresAccessApproval && this.props.currentSettings.audience !== SharingAudience.existing;
        this._shareTargetsData = this._getShareTargetsData();

        if (!isLoadingData(this.props.sharingInformation)) {
            // Record which share targets are available.
            const targets = [];
            for (const target of this._shareTargetsData) {
                targets.push(target.shareType);
            }
            this.props.onShareTargetsRendered(targets);
        }

        return accessRequestsRequired ?
            (

                <div className='od-ShareTargets'>
                    <TooltipHost
                        content={ stringsResx.accessRequestTooltip }
                        calloutProps={ { calloutWidth: 300, directionalHint: DirectionalHint.topCenter } }
                    >
                        <ul
                            className='od-ShareTargets-items'
                            aria-label={ stringsResx.otherWaysToShareLabel }
                        >
                            { this._renderTargets() }
                        </ul>
                    </TooltipHost>
                </div>
            ) :
            (
                <div className='od-ShareTargets'>
                    <ul
                        className='od-ShareTargets-items'
                        aria-label={ stringsResx.otherWaysToShareLabel }
                    >
                        { this._renderTargets() }
                    </ul>
                </div>
            );
    }

    private _renderTargets(): JSX.Element[] {
        let listItems: JSX.Element[] = [];
        const accessRequestsRequired = this.props.sharingInformation.requiresAccessApproval && this.props.currentSettings.audience !== SharingAudience.existing;

        for (const target of this._shareTargetsData) {
            let image: JSX.Element;
            const icon = target.icon;
            const iconClass = icon.split(':', 2)[1];

            /**
             * Determine styles based on access requests being enabled,
             * and thus share targets being disabled or not.
             */
            const isDisabled = accessRequestsRequired || isLoadingData(this.props.sharingInformation);
            const iconClasses = 'od-ShareTargets-icon ' + (isDisabled ? 'od-ShareTargets-disabled' : '');
            const labelClasses = `od-ShareTargets-itemText ms-font-xs ${isDisabled ? 'od-ShareTargets-disabled' : ''}`;

            if (icon.indexOf('font:') > -1) {
                image = <Icon iconName={ iconClass } className={ iconClasses } />;
            } else {
                image = <img src={ iconClass } className={ iconClasses } />;
            }

            listItems.push(
                <li key={ target.shareType } className='od-ShareTargets-item'>
                    <button
                        className='od-ShareTargets-target'
                        disabled={ isDisabled }
                        onClick={ target.action }
                    >
                        <div
                            className={ 'od-ShareTargets-itemImage' }
                        >
                            { image }
                        </div>
                        <div className={ labelClasses }>{ target.label }</div>
                    </button>
                </li>
            );
        }

        return listItems;
    }

    private _getShareTargetsData(): Array<IShareTargetData> {
        const targets: Array<IShareTargetData> = [
            {
                label: stringsResx.copyLinkLabel,
                icon: 'font:Link',
                bgColor: 'ms-bgColor-themePrimary',
                shareType: ShareType.copy,
                action: this._onClick.bind(this, ShareType.copy)
            }
        ];

        const outlookAvailability = this._determineMailShareTarget(this.props.clientId);
        if (outlookAvailability === OutlookAvailability.available) {
            targets.push({
                label: stringsResx.outlookLabel,
                icon: 'font:OutlookLogo',
                bgColor: 'ms-bgColor-themePrimary',
                shareType: ShareType.outlook,
                action: this._onClick.bind(this, ShareType.outlook)
            });
        } else if (outlookAvailability === OutlookAvailability.other) {
            targets.push({
                label: stringsResx.nonOutlookLabel,
                icon: 'font:Mail',
                bgColor: 'ms-bgColor-themePrimary',
                shareType: ShareType.nonOutlook,
                action: this._onClick.bind(this, ShareType.nonOutlook)
            });
        }

        if (this._isTeamsShareTargetEnabled()) {
            targets.push({
                label: stringsResx.teamsLabel,
                icon: 'font:TeamsLogo',
                bgColor: 'ms-bgColor-themePrimary',
                shareType: ShareType.teams,
                action: this._onClick.bind(this, ShareType.teams)
            });
        }

        if (this._isMoreAppsShareTargetEnabled()) {
            targets.push({
                label: stringsResx.moreAppsLabel,
                icon: 'font:More',
                bgColor: 'ms-bgColor-themePrimary',
                shareType: ShareType.moreApps,
                action: this._onClick.bind(this, ShareType.moreApps)
            });
        }

        return targets;
    }

    private _onClick(shareType: ShareType, evt: React.SyntheticEvent<{}>): void {
        this.props.onShareTargetClicked(shareType);
    }

    @autobind
    private _determineMailShareTarget(clientId: ClientId): OutlookAvailability {
        /**
         * - Outlook share target, that opens OWA, is available in ODSP, Office.com,
         *   and WAC if the user has it available to them (meaning they have an
         *   Exchange license for ODB).
         * - Other partners have the option to implement CheckSendViaOutlookAvailability
         *   which will determine if we show an Outlook button (that they control) or a
         *   generic "Mail" button (Office clients only).
         */
        if (ClientIdHelper.showOWAShareTarget(this.props.clientId) && this.props.outlookAvailability === OutlookAvailability.available) {
            return OutlookAvailability.available;
        } else {
            try {
                const externalJavaScript: any = window.external;
                const outlookAvailability: OutlookAvailability = externalJavaScript.CheckSendViaOutlookAvailability();
                return outlookAvailability;
            } catch (error) {
                /**
                 * If "CheckSendViaOutlookAvailability" hasn't been implemented, then don't
                 * show the target.
                 */
                return OutlookAvailability.none;
            }
        }
    }

    private _isTeamsShareTargetEnabled() {
        try {
            const externalJavaScript: any = window.external;
            return externalJavaScript.IsTeamsEnabled();
        } catch (error) {
            // If "IsTeamsEnabled" hasn't been implemented, then don't show the target.
            return false;
        }
    }

    private _isMoreAppsShareTargetEnabled() {
        try {
            const externalJavaScript: any = window.external;
            return externalJavaScript.IsMoreAppsEnabled();
        } catch (error) {
            // If "IsMoreAppsEnabled" hasn't been implemented, then don't show the target.
            return false;
        }
    }
}